import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';

// My Invites — unified view of pending group + course invitations (design/018).
export default function InvitesPage() {
  const navigate = useNavigate();
  // Pending invites stop counting in the bell once seen here (V62).
  useEffect(() => {
    api.notificationsInvitesSeen()
      .then(() => window.dispatchEvent(new Event('badge-refresh')))
      .catch(() => {});
  }, []);
  const { loading, error, data, reload } = useAsync(async () => {
    const [groups, courses] = await Promise.all([
      api.listMyInvites().then((x) => (Array.isArray(x) ? x : [])).catch(() => []),
      api.listMyCourseInvites().then((x) => (Array.isArray(x) ? x : [])).catch(() => []),
    ]);
    return { groups, courses };
  }, []);

  const acceptGroup = async (inv) => {
    try { await api.acceptInvite(inv.token); if (inv.group?.id) navigate(`/group/${inv.group.id}`); else reload(); }
    catch (e) { alert(e.message); }
  };
  const declineGroup = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    try { await api.declineInvite(inv.token); reload(); } catch (e) { alert(e.message); }
  };

  // Course invites accept by short code (the raw token isn't exposed in the list).
  const acceptCourse = async (inv) => {
    try {
      const r = await api.acceptCourseInvite(inv.shortCode);
      if (r?.requiresCheckout && r.checkoutUrl) window.location.assign(r.checkoutUrl);
      else navigate(`/learn/course/${inv.courseId}`);
    }
    catch (e) { alert(e.message); }
  };
  const declineCourse = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    try { await api.declineCourseInvite(inv.shortCode); reload(); } catch (e) { alert(e.message); }
  };

  const nothing = !loading && !error && data.groups.length === 0 && data.courses.length === 0;

  return (
    <div className="page">
      <PageHead title="My Invites" sub="Groups and courses you've been invited to join" />
      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : nothing ? (
        <Section><Empty>No pending invites.</Empty></Section>
      ) : (
        <>
          {data.courses.length > 0 && (
            <Section title="Courses">
              {data.courses.map((inv) => (
                <div key={inv.id} className="member-row">
                  <div style={{ flex: 1 }}>
                    <div className="nm">{inv.courseTitle || 'A course'}</div>
                    <div className="em">from {inv.inviterName || 'a publisher'}</div>
                  </div>
                  <button className="btn btn-sm" onClick={() => declineCourse(inv)}>Decline</button>
                  <button className="btn btn-sm btn-primary" onClick={() => acceptCourse(inv)}>Accept &amp; enroll</button>
                </div>
              ))}
            </Section>
          )}

          {data.groups.length > 0 && (
            <Section title="Groups">
              {data.groups.map((inv) => (
                <div key={inv.token || inv.id} className="member-row">
                  <div style={{ flex: 1 }}>
                    <div className="nm">{inv.group?.name || inv.groupName || 'A group'}</div>
                    <div className="em">{inv.inviterUsername ? `from @${inv.inviterUsername}` : (inv.inviteeEmail || '')}</div>
                  </div>
                  <button className="btn btn-sm" onClick={() => declineGroup(inv)}>Decline</button>
                  <button className="btn btn-sm btn-primary" onClick={() => acceptGroup(inv)}>Accept</button>
                </div>
              ))}
            </Section>
          )}
        </>
      )}
    </div>
  );
}
